import { UploadStatus } from '@aus-platform/design-system';
import { isEmpty, isNil } from 'lodash';
import { useEffect, useRef } from 'react';
import { useAppDispatch } from 'app/hooks';
import { setUploadStatusList } from 'shared/upload-widget-slices';
import type { UploadFileSetItem } from 'shared/upload-widget-slices/types';
import {
  activeImageSetUploadStages,
  finishedImageSetUploadStages,
  PROGRESS_DISPATCH_INTERVAL_MS,
  runningUploadStatuses,
  UPLOAD_INTERRUPTED_MESSAGE,
  UPLOAD_QUEUE_CHECK_INTERVAL_MS,
} from '../constants';
import { createImageSetUploader } from '../image-set-uploader';
import { uploadProcessIdLocalStorageManager } from '../image-upload-handler';
import type { ImageSetUploader, ImageSetUploadSnapshot } from '../types';
import {
  calculateUploadProgress,
  mapImageSetStageToUploadStatus,
  removeOrphanIds,
  removeProcessId,
  updateProcessId,
} from '../utils';

type UseImageSetUploadReturn = {
  pauseUpload: VoidFunction;
  resumeUpload: VoidFunction;
  cancelUpload: VoidFunction;
};

/**
 * Runs the image set upload for one Upload Widget row.
 *
 * It joins the cross-tab upload queue, starts the upload when the row is first in the queue,
 * and writes the upload's progress to the row's `uploadStatusList` entry. A row that remounts
 * after its upload was stopped is marked as interrupted so it can be removed.
 *
 * @param uploadItem - The file set to upload.
 * @param uploadStatus - The row's status when it mounts. Only a pending row starts an upload.
 * @returns Handlers for the row's pause, resume and cancel actions.
 */
export const useImageSetUpload = (
  uploadItem: UploadFileSetItem,
  uploadStatus?: UploadStatus,
): UseImageSetUploadReturn => {
  // Dispatchers.
  const dispatch = useAppDispatch();

  // Refs.
  const uploaderRef = useRef<ImageSetUploader | null>(null);
  const processIdRef = useRef<string>(uploadItem.id);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const queueCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Helpers.
  const syncUploadStatus = (snapshot: ImageSetUploadSnapshot) => {
    const { progress, speed, timeLeft, timeTaken, counts } =
      calculateUploadProgress(snapshot);

    dispatch(
      setUploadStatusList((uploadStatusList) =>
        uploadStatusList.map((item) =>
          item.id === uploadItem.id
            ? {
                ...item,
                uploadStatus: mapImageSetStageToUploadStatus(snapshot.stage),
                progress,
                speed,
                timeLeft,
                timeTaken,
                counts,
                error: snapshot.error,
              }
            : item,
        ),
      ),
    );
  };

  const markUploadInterrupted = () => {
    dispatch(
      setUploadStatusList((uploadStatusList) =>
        uploadStatusList.map((item) =>
          item.id === uploadItem.id
            ? {
                ...item,
                uploadStatus: UploadStatus.Error,
                error: UPLOAD_INTERRUPTED_MESSAGE,
              }
            : item,
        ),
      ),
    );
  };

  const stopQueueCheck = () => {
    if (!isNil(queueCheckIntervalRef.current)) {
      clearInterval(queueCheckIntervalRef.current);
      queueCheckIntervalRef.current = null;
    }
  };

  const releaseProcessId = () => {
    stopQueueCheck();
    removeProcessId(
      true,
      processIdRef.current,
      heartbeatIntervalRef,
      processIdRef,
    );
  };

  // useEffects.
  useEffect(() => {
    // A remounted row that is no longer pending must not upload again.
    if (uploadStatus !== UploadStatus.Pending) {
      if (
        !isNil(uploadStatus) &&
        runningUploadStatuses.includes(uploadStatus)
      ) {
        markUploadInterrupted();
      }
      return;
    }

    const uploader = createImageSetUploader({
      files: uploadItem.files,
      iterationDatasetId: uploadItem.iterationDatasetId,
      onChange: (snapshot) => {
        syncUploadStatus(snapshot);

        if (finishedImageSetUploadStages.includes(snapshot.stage)) {
          releaseProcessId();
        }
      },
    });
    uploaderRef.current = uploader;

    processIdRef.current = uploadItem.id;
    updateProcessId(processIdRef.current, heartbeatIntervalRef);

    queueCheckIntervalRef.current = setInterval(() => {
      const processIdsWithTimestamps =
        uploadProcessIdLocalStorageManager.getProcessObject();

      if (isEmpty(processIdsWithTimestamps)) {
        return;
      }

      if (processIdsWithTimestamps[0].processId === processIdRef.current) {
        stopQueueCheck();
        void uploader.start();
        return;
      }

      removeOrphanIds(
        processIdsWithTimestamps,
        heartbeatIntervalRef,
        processIdRef,
      );
    }, UPLOAD_QUEUE_CHECK_INTERVAL_MS);

    const progressSyncInterval = setInterval(() => {
      const snapshot = uploader.getSnapshot();

      if (activeImageSetUploadStages.includes(snapshot.stage)) {
        syncUploadStatus(snapshot);
      }
    }, PROGRESS_DISPATCH_INTERVAL_MS);

    // "pagehide" only fires when the page really goes away, unlike "beforeunload".
    window.addEventListener('pagehide', releaseProcessId);

    return () => {
      clearInterval(progressSyncInterval);
      window.removeEventListener('pagehide', releaseProcessId);
      uploader.dispose();
      uploaderRef.current = null;
      releaseProcessId();
    };
  }, []);

  // Handlers.
  const pauseUpload = () => {
    uploaderRef.current?.pause();
  };

  const resumeUpload = () => {
    void uploaderRef.current?.resume();
  };

  const cancelUpload = () => {
    void uploaderRef.current?.cancel();
  };

  return { pauseUpload, resumeUpload, cancelUpload };
};
